import React from 'react';
import { Popconfirm, Row, Tooltip } from 'antd';
import { Eye } from 'iconsax-react';
import { PiPencil } from 'react-icons/pi';
import { RiErrorWarningLine } from 'react-icons/ri';
import { IoEarth } from 'react-icons/io5';
import { Delete } from 'react-iconly';
import { Link } from 'react-router-dom';
import { BsCopy } from 'react-icons/bs';
import isEmpty from 'lodash/isEmpty';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import urlPagePushNotificationDetails from '../../../../../../urls/urlPagePushNotificationDetails';
import urlPageTemplateDetails from '../../../../../../urls/urlPageTemplateDetails';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import { Permissions } from '../../../../../../const/permissions';
import getDictionaryIdFromDynamicData from '../../../../../../lib/getDictionaryIdFromDynamicData';
import { HasRights, PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import CollapsibleText from '../../../../../../components/CollapsibleText';
import { EMPTY_IMAGE_PLACEHOLDER } from '../../../../../../const/system';



export const PUSH_NOTIFICATIONS_SOURCE = 'push-notifications';


export const getPushNotificationsTableColumns = (
  handlePreviewDictionary,
  handleEdit,
  handleDelete,
  handleClone,
  handleImageCardClick,
  tableSize,
  canViewTemplateColumn,
) => [
  {
    title: <IntlMessages id="ui-general-id" />,
    dataIndex: 'id',
    align: 'center',
    width: tableSize.width * 0.08,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-preview" />,
    dataIndex: 'preview',
    align: 'center',
    width: tableSize.width * 0.09,
    render: (value, item) => (
      <LazyLoadImage
        onClick={() => {
          if (!isEmpty(item?.preview)) {
            handleImageCardClick(item.preview);
          }
        }}
        style={!isEmpty(item?.preview) ? { cursor: 'pointer' } : {}}
        className='hp-cursor-pointer'
        effect='blur'
        key={item?.id}
        alt="push notifications preview"
        placeholderSrc={EMPTY_IMAGE_PLACEHOLDER}
        src={value ? `${value}?time=${Date.now()}` : EMPTY_IMAGE_PLACEHOLDER}
      />
    ),
  },
  {
    title: <IntlMessages id="ui-general-title" />,
    dataIndex: 'title',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.2,
    render: (item) => <CollapsibleText text={item} maxLength={200} />,
  },
  ...canViewTemplateColumn ? [ {
    title: <IntlMessages id="ui-general-template" />,
    align: 'center',
    dataIndex: 'template',
    sorter: true,
    width: tableSize.width * 0.2,
    render: (template) => {
      return (HasRights([ Permissions.CONSTRUCTORS.TEMPLATES.FIND ]) ?
        <Link
          to={urlPageTemplateDetails({ templateId: template?.data?.id })}
          target="_blank"
        >
          <CollapsibleText text={template?.data?.title} maxLength={200} />
        </Link> : <CollapsibleText text={template?.data?.title} maxLength={200} />
      );
    },
  },
  ] : [],
  {
    title: <IntlMessages id="ui-general-action" />,
    key: 'action',
    align: 'center',
    width: tableSize.width * 0.12,
    render: (item) => {
      const dictionaryId = getDictionaryIdFromDynamicData(item?.dynamic_data?.data);
      const dictionaryExists = !!dictionaryId;

      return (
        <Row className="da-h-100" align="middle" justify="center">
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.FIND ]}>
            <Tooltip placement="top" title={<IntlMessages id="ui-general-view" />}>
              <div className="hp-text-right">
                <Link
                  to={urlPagePushNotificationDetails({ notificationId: item.id })}
                  target="_blank"
                >
                  <Eye
                    size={20}
                    data-testid="view-icon"
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                  />
                </Link>
              </div>
            </Tooltip>
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.EDIT ]}>
            {!item?.has_schedules ? (
              <Tooltip placement="top" title={<IntlMessages id="ui-general-edit" />}>
                <div className="hp-text-right">
                  <PiPencil
                    data-testid="edit-icon"
                    onClick={() => handleEdit(item.id)}
                    size={20}
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            ) : (
              <Tooltip placement="top" title={<IntlMessages id="push-notifications-unable-update" />}>
                <div className="hp-text-right">
                  <PiPencil
                    disabled
                    size={20}
                    data-testid="edit-icon"
                    className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.CLONE ]}>
            <Tooltip placement="top" title={<IntlMessages id="ui-general-clone" />}>
              <div className="hp-text-right">
                <BsCopy
                  onClick={() => handleClone(item.id)}
                  size={18}
                  data-testid="clone-icon"
                  className="hp-cursor-pointer hp-transition hp-hover-text-color-success-1 hp-text-color-black-80 hp-m-4"
                />
              </div>
            </Tooltip>
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.DELETE ]}>
            {!item?.has_schedules ? (
              <Popconfirm
                title={<IntlMessages id="ui-general-delete-confirm-message" />}
                placement="topLeft"
                onConfirm={() => handleDelete(item.id)}
                okText={<IntlMessages id="ui-general-yes" />}
                cancelText={<IntlMessages id="ui-general-no" />}
                icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                okButtonProps={{ danger: true }}
              >
                <Tooltip placement="top" title={<IntlMessages id="ui-general-delete" />}>
                  <div className="hp-text-right">
                    <Delete
                      size={20}
                      data-testid="delete-icon"
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              </Popconfirm>
            ) : (
              <Tooltip placement="top" title={<IntlMessages id="push-notifications-unable-delete" />}>
                <div className="hp-text-right">
                  <Delete
                    disabled
                    size={20}
                    data-testid="delete-icon"
                    className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW ]}>
            {dictionaryExists && (
              <Tooltip placement="top" title={<IntlMessages id="ui-general-dictionary" />}>
                <div className="hp-text-right">
                  <IoEarth
                    size={20}
                    data-testid="dictionary-icon"
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                    onClick={() => handlePreviewDictionary(dictionaryId)}
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
        </Row>
      );
    },
  },
];
